<template>
  <div class="select-remote" v-click-outside="closeDropdown">
    <div class="select-input-wrapper" @mousedown="handleMouseDown">
      <input
        ref="searchInput"
        v-model="searchText"
        type="text"
        class="form-control select-input"
        :class="{ 'is-focus': showDropdown }"
        :placeholder="currentPlaceholder"
        :disabled="disabled"
        :readonly="!filterable"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.down.prevent="navigateDown"
        @keydown.up.prevent="navigateUp"
        @keydown.enter.prevent="selectHighlighted"
        @keydown.esc="closeDropdown"
      />
      <i
        v-if="showSearchClearBtn"
        class="bx bx-x select-search-clear"
        @mousedown.stop.prevent="clearSearchText"
      ></i>
      <i class="bx bx-chevron-down select-arrow" :class="{ rotate: showDropdown }"></i>
      <i
        v-if="showClearBtn"
        class="bx bx-x select-clear"
        @mousedown.stop.prevent="clearSelection"
      ></i>
    </div>

    <transition name="dropdown">
      <div v-show="showDropdown" class="select-dropdown" ref="dropdown" @scroll="handleScroll">
        <div v-if="loading && !loadingMore" class="select-loading">
          <i class="bx bx-loader-alt bx-spin"></i> {{ loadingText }}
        </div>
        <div v-else-if="!filteredOptions.length && !loading" class="select-empty">
          {{ emptyText }}
        </div>
        <ul v-else class="select-options" ref="optionsList">
          <li
            v-for="(option, index) in filteredOptions"
            :key="option[valueKey]"
            class="select-option"
            :class="{
              'is-selected': isSelected(option),
              'is-highlighted': highlightedIndex === index,
            }"
            @mousedown.prevent="selectOption(option)"
            @mouseenter="highlightedIndex = index"
          >
            <slot name="option" :option="option">
              {{ option[labelKey] }}
            </slot>
          </li>
          <li v-if="waitingLoadMore" class="select-loading-more select-waiting">
            <i class="bx bx-time"></i> Đang chờ tải thêm...
          </li>
          <li v-else-if="loadingMore" class="select-loading-more">
            <i class="bx bx-loader-alt bx-spin"></i> Đang tải thêm...
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
  export default {
    name: 'SelectRemote',
    props: {
      value: {
        type: [String, Number, Object],
        default: null,
      },
      options: {
        type: Array,
        default: () => [],
      },
      placeholder: {
        type: String,
        default: 'Chọn một mục',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      clearable: {
        type: Boolean,
        default: true,
      },
      filterable: {
        type: Boolean,
        default: true,
      },
      remote: {
        type: Boolean,
        default: false,
      },
      remoteMethod: {
        type: Function,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      loadingText: {
        type: String,
        default: 'Đang tải...',
      },
      emptyText: {
        type: String,
        default: 'Không có dữ liệu',
      },
      valueKey: {
        type: String,
        default: 'id',
      },
      labelKey: {
        type: String,
        default: 'text',
      },
      minSearchLength: {
        type: Number,
        default: 0,
      },
      debounceDelay: {
        type: Number,
        default: 300,
      },
      // Props for pagination/loadmore
      enableLoadMore: {
        type: Boolean,
        default: false,
      },
      currentPage: {
        type: Number,
        default: 1,
      },
      hasMore: {
        type: Boolean,
        default: false,
      },
      // Default selected label
      defaultLabel: {
        type: String,
        default: '',
      },
    },
    data() {
      return {
        showDropdown: false,
        searchText: '',
        highlightedIndex: -1,
        debounceTimer: null,
        loadMoreTimer: null,
        loadingMore: false,
        waitingLoadMore: false,
        isFocused: false,
        isComposing: false,
        mouseDownOnInput: false,
      }
    },
    computed: {
      filteredOptions() {
        if (this.remote) {
          return this.options
        }
        if (!this.filterable || !this.searchText) {
          return this.options
        }
        const search = this.searchText.toLowerCase()
        return this.options.filter(option => {
          const label = option[this.labelKey] || ''
          return label.toLowerCase().includes(search)
        })
      },
      selectedOption() {
        if (!this.value) return null
        return this.options.find(opt => opt[this.valueKey] === this.value)
      },
      showClearBtn() {
        return this.clearable && !this.disabled && this.value
      },
      showSearchClearBtn() {
        return (
          this.filterable &&
          this.searchText &&
          this.searchText.length > 0 &&
          this.showDropdown &&
          !this.disabled
        )
      },
      currentPlaceholder() {
        if (this.selectedOption && !this.isFocused) {
          return ''
        }
        return this.placeholder
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(val) {
          this.updateSearchText()
        },
      },
      options: {
        deep: true,
        handler() {
          this.updateSearchText()
        },
      },
      defaultLabel: {
        immediate: true,
        handler(val) {
          if (val && !this.selectedOption && this.value) {
            this.searchText = val
          }
        },
      },
    },
    directives: {
      'click-outside': {
        bind(el, binding, vnode) {
          el.clickOutsideEvent = function (event) {
            if (!(el === event.target || el.contains(event.target))) {
              binding.value(event)
            }
          }
          document.body.addEventListener('click', el.clickOutsideEvent)
        },
        unbind(el) {
          document.body.removeEventListener('click', el.clickOutsideEvent)
        },
      },
    },
    methods: {
      updateSearchText() {
        if (!this.isFocused) {
          if (this.value && this.selectedOption) {
            this.searchText = this.selectedOption[this.labelKey]
          } else if (this.value && this.defaultLabel) {
            this.searchText = this.defaultLabel
          } else if (!this.value) {
            this.searchText = ''
          }
        }
      },
      handleMouseDown(event) {
        if (this.disabled) return
        // Prevent default to avoid input blur
        event.preventDefault()

        // Check if clicking on clear button or search clear button
        if (
          event.target.classList.contains('select-clear') ||
          event.target.classList.contains('select-search-clear')
        ) {
          return
        }

        this.mouseDownOnInput = true

        if (!this.showDropdown) {
          this.openDropdown()
        } else if (!this.filterable) {
          this.closeDropdown()
        }

        this.$refs.searchInput.focus()
      },
      openDropdown() {
        if (this.disabled || this.showDropdown) return

        this.showDropdown = true
        this.highlightedIndex = this.selectedOption
          ? this.filteredOptions.findIndex(opt => opt[this.valueKey] === this.value)
          : 0

        this.$nextTick(() => {
          // Khi có giá trị, search với giá trị đó thay vì clear
          const hasExistingValue = this.value && (this.selectedOption || this.defaultLabel)

          if (this.filterable && this.isFocused && !hasExistingValue) {
            this.searchText = ''
          }

          if (this.remote && this.remoteMethod) {
            const query = this.searchText || ''
            // Luôn gọi API ngay cả khi không có query để load danh sách mặc định
            if (query.length >= this.minSearchLength || query === '') {
              this.remoteMethod(query, 1)
            }
          }

          this.scrollToHighlighted()
        })
      },
      closeDropdown() {
        this.showDropdown = false
        this.highlightedIndex = -1
        this.updateSearchText()
      },
      handleFocus(event) {
        this.isFocused = true

        if (!this.mouseDownOnInput) {
          this.openDropdown()
        }

        this.mouseDownOnInput = false
      },
      handleBlur(event) {
        this.isFocused = false
        this.mouseDownOnInput = false
      },
      handleInput(event) {
        if (this.isComposing) return

        const value = event.target.value
        this.searchText = value
        this.highlightedIndex = 0

        if (!this.showDropdown) {
          this.showDropdown = true
        }

        if (this.remote && this.remoteMethod) {
          if (value.length < this.minSearchLength) {
            return
          }

          if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
          }

          this.debounceTimer = setTimeout(() => {
            this.remoteMethod(value, 1)
          }, this.debounceDelay)
        }
      },
      selectOption(option) {
        this.searchText = option[this.labelKey]
        this.$emit('input', option[this.valueKey])
        this.$emit('change', option[this.valueKey], option)
        this.closeDropdown()
        this.$refs.searchInput.blur()
      },
      clearSelection() {
        this.searchText = ''
        this.$emit('input', null)
        this.$emit('change', null, null)
        this.$emit('clear')
        this.$nextTick(() => {
          if (this.showDropdown) {
            this.closeDropdown()
          }
        })
      },
      clearSearchText() {
        this.searchText = ''
        this.highlightedIndex = 0
        this.$refs.searchInput.focus()

        // Nếu remote search, gọi lại với query rỗng
        if (this.remote && this.remoteMethod) {
          this.remoteMethod('', 1)
        }
      },
      isSelected(option) {
        return this.value && option[this.valueKey] === this.value
      },
      navigateDown() {
        if (!this.showDropdown) {
          this.openDropdown()
          return
        }
        if (this.highlightedIndex < this.filteredOptions.length - 1) {
          this.highlightedIndex++
          this.scrollToHighlighted()
        }
      },
      navigateUp() {
        if (this.highlightedIndex > 0) {
          this.highlightedIndex--
          this.scrollToHighlighted()
        }
      },
      selectHighlighted() {
        if (this.highlightedIndex >= 0 && this.filteredOptions[this.highlightedIndex]) {
          this.selectOption(this.filteredOptions[this.highlightedIndex])
        }
      },
      scrollToHighlighted() {
        this.$nextTick(() => {
          const list = this.$refs.optionsList
          if (!list) return
          const highlighted = list.children[this.highlightedIndex]
          if (highlighted) {
            highlighted.scrollIntoView({ block: 'nearest' })
          }
        })
      },
      handleScroll(event) {
        const dropdown = this.$refs.dropdown
        if (!dropdown) {
          return
        }

        if (!this.enableLoadMore) {
          return
        }

        if (!this.hasMore) {
          return
        }

        if (this.loadingMore || this.waitingLoadMore) {
          return
        }

        if (this.loading) {
          return
        }

        const scrollTop = dropdown.scrollTop
        const scrollHeight = dropdown.scrollHeight
        const clientHeight = dropdown.clientHeight

        // Load more when scrolled to near bottom (threshold 20px)
        const threshold = 20
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold

        if (isNearBottom) {
          // Clear previous timer nếu có
          if (this.loadMoreTimer) {
            clearTimeout(this.loadMoreTimer)
            this.waitingLoadMore = false
          }

          // Hiển thị trạng thái đang chờ
          this.waitingLoadMore = true

          // Đợi 1 giây trước khi gọi API
          this.loadMoreTimer = setTimeout(() => {
            // Kiểm tra lại vẫn còn ở cuối dropdown
            const currentScrollTop = dropdown.scrollTop
            const currentIsNearBottom = currentScrollTop + clientHeight >= scrollHeight - threshold

            if (currentIsNearBottom && this.hasMore && !this.loadingMore) {
              this.waitingLoadMore = false
              this.loadMore()
            } else {
              console.error('Not at bottom anymore or already loading, cancelled')
              this.waitingLoadMore = false
            }
          }, 2000) // 1 giây
        }
      },
      loadMore() {
        if (!this.hasMore || this.loadingMore || !this.remoteMethod) {
          return
        }

        const nextPage = this.currentPage + 1

        this.loadingMore = true

        // Chỉ gọi remoteMethod, không emit event nữa để tránh gọi 2 lần
        this.remoteMethod(this.searchText || '', nextPage, () => {
          this.loadingMore = false
        })
      },
    },
    beforeDestroy() {
      // Clear timers khi component bị destroy
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }
      if (this.loadMoreTimer) {
        clearTimeout(this.loadMoreTimer)
        this.waitingLoadMore = false
      }
    },
  }
</script>

<style scoped>
  .select-remote {
    position: relative;
    width: 100%;
  }

  .select-input-wrapper {
    position: relative;
  }

  .select-input {
    width: 100%;
    padding-right: 30px;
    cursor: pointer;
    user-select: none;
  }

  .select-input.is-focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
  }

  .select-input:disabled {
    cursor: not-allowed;
    background-color: #e9ecef;
  }

  .select-input[readonly] {
    cursor: pointer;
  }

  .select-arrow,
  .select-clear,
  .select-search-clear {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    pointer-events: none;
    transition: transform 0.3s;
    font-size: 16px;
  }

  .select-arrow.rotate {
    transform: translateY(-50%) rotate(180deg);
  }

  .select-clear {
    cursor: pointer;
    pointer-events: auto;
    right: 30px;
    font-size: 18px;
    color: #adb5bd;
    z-index: 1;
  }

  .select-clear:hover {
    color: #495057;
  }

  .select-search-clear {
    cursor: pointer;
    pointer-events: auto;
    right: 30px;
    font-size: 16px;
    color: #adb5bd;
    z-index: 2;
    background: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .select-search-clear:hover {
    color: #495057;
    background: #f8f9fa;
  }

  .select-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ced4da;
    border-radius: 0.25rem;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    max-height: 300px;
    overflow-y: auto;
    z-index: 1000;
  }

  .select-loading,
  .select-empty {
    padding: 12px 16px;
    text-align: center;
    color: #6c757d;
    font-size: 14px;
  }

  .select-loading i,
  .select-loading-more i {
    margin-right: 8px;
  }

  .select-loading-more {
    padding: 8px 16px;
    text-align: center;
    color: #6c757d;
    font-size: 13px;
  }

  .select-loading-more.select-waiting {
    color: #ffc107;
    font-weight: 500;
  }

  .select-loading-more.select-waiting i {
    animation: pulse 1.5s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }

    50% {
      opacity: 0.5;
    }
  }

  .select-options {
    list-style: none;
    margin: 0;
    padding: 4px 0;
  }

  .select-option {
    padding: 8px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    font-size: 13px;
    color: #212529;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .select-option:hover,
  .select-option.is-highlighted {
    background-color: #f8f9fa;
  }

  .select-option.is-selected {
    background-color: #007bff;
    color: white;
    font-weight: 500;
  }

  .select-option.is-selected:hover {
    background-color: #0056b3;
  }

  /* Dropdown animation */
  .dropdown-enter-active,
  .dropdown-leave-active {
    transition: all 0.3s ease;
  }

  .dropdown-enter,
  .dropdown-leave-to {
    opacity: 0;
    transform: translateY(-10px);
  }

  /* Scrollbar styling */
  .select-dropdown::-webkit-scrollbar {
    width: 6px;
  }

  .select-dropdown::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  .select-dropdown::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  .select-dropdown::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
</style>
